import React from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { ILayoutItem, IReportTile } from '../../types';
import { ResizableGrid } from '@/components/resizable-grid';
import { ReportTileContainer } from '../ReportTileContainer';
import { REPORT_GRID_CONFIG, syncLayoutWithTiles } from './helpers';
import { isEqual } from 'lodash';

interface StaticFlowTileWrapperProps {
  tile: IReportTile;
  report: any;
  isReadOnly?: boolean;
  layout: { w: number; h: number };
  isStaticReport?: boolean;
  viewMode?: boolean;
  handleTileAction: (_tileId: string, _payload: any) => void;
}

const StaticTableTileWrapper = React.memo(
  ({
    tile,
    report,
    isReadOnly,
    layout,
    isStaticReport,
    viewMode,
    handleTileAction,
  }: StaticFlowTileWrapperProps) => {
    return (
      <ReportTileContainer
        tile={tile}
        report={report}
        isReadOnly={isReadOnly}
        isBlockHovering={true}
        isStaticReport={isStaticReport}
        layout={layout}
        viewMode={viewMode}
        handleTileAction={handleTileAction}
      />
    );
  },
  () => true
);

// Type definition for API response
interface CustomReport {
  id: number;
  layout: {
    data: ILayoutItem[];
  };
  tiles: IReportTile[];
}

interface FlowReportLayoutProps {
  tiles: IReportTile[];
  savedLayout: ILayoutItem[] | undefined;
  customReport: CustomReport;
  isReadOnly?: boolean;
  isStaticReport?: boolean;
  viewMode?: boolean;
  handleTileAction: (_tileId: string, _payload: any) => void;
}

const MemoizedReportTileContainer = React.memo(
  ReportTileContainer,
  (prevProps, nextProps) => {
    return (
      prevProps.tile.id === nextProps.tile.id &&
      prevProps.tile.subject_type === nextProps.tile.subject_type &&
      !prevProps.isReadOnly === !nextProps.isReadOnly &&
      isEqual(prevProps.layout, nextProps.layout) &&
      prevProps.isBlockHovering === nextProps.isBlockHovering
    );
  }
);

export const FlowReportLayout = ({
  tiles,
  savedLayout,
  customReport,
  isReadOnly,
  isStaticReport,
  viewMode,
  handleTileAction,
}: FlowReportLayoutProps) => {
  // Get the original order from the config/layout
  const orderedLayout = syncLayoutWithTiles(
    tiles,
    savedLayout || [],
    isStaticReport
  );

  // Sort tiles by their order in the layout
  const orderedComponents = orderedLayout
    .map((layoutItem) => {
      const tile = tiles.find((t) => t.id.toString() === layoutItem.key);
      return tile ? { tile, layoutItem } : null;
    })
    .filter(Boolean);

  if (orderedComponents.length === 0) return null;

  return (
    <Flex direction="column" gap="14px" width="100%">
      {orderedComponents.map(({ tile, layoutItem }) => {
        const stableKey = `ordered-${tile.id}`;

        if (tile.metadata.componentType === 'chart') {
          // Render chart with ResizableGrid
          return (
            <Box key={stableKey} width="100%" mb="14px">
              <ResizableGrid
                columns={REPORT_GRID_CONFIG.GRID.COLUMNS}
                rowHeight={REPORT_GRID_CONFIG.GRID.ROW_HEIGHT}
                config={[
                  {
                    ...layoutItem,
                    render: ({ isHovering }) => (
                      <MemoizedReportTileContainer
                        tile={tile}
                        report={
                          customReport as unknown as Record<string, unknown>
                        }
                        isReadOnly={isReadOnly}
                        isBlockHovering={isHovering}
                        isStaticReport={isStaticReport}
                        layout={{ w: layoutItem.w, h: layoutItem.h }}
                        viewMode={viewMode}
                        handleTileAction={handleTileAction}
                      />
                    ),
                  },
                ]}
                onLayoutChange={() => {}} // No layout changes in static view
                isPrintView={isReadOnly}
                reactGridLayoutProps={{
                  isDraggable: false,
                  isDroppable: false,
                  isResizable: false,
                }}
                itemProps={{
                  showBoxShadow: false,
                  showDragHandle: false,
                  showBorder: true,
                }}
              />
            </Box>
          );
        } else {
          // Render table component
          return (
            <Box
              key={stableKey}
              width="100%"
              minHeight={`${
                layoutItem.h * REPORT_GRID_CONFIG.GRID.ROW_HEIGHT
              }px`}
              border="1px solid"
              borderColor="border-color"
              borderRadius="sm"
              padding="16px"
              backgroundColor="white"
              boxShadow="sm"
              display="flex"
              flexDirection="column"
              position="relative"
            >
              <StaticTableTileWrapper
                tile={tile}
                report={customReport}
                isReadOnly={isReadOnly}
                isStaticReport={isStaticReport}
                layout={{ w: layoutItem.w, h: layoutItem.h }}
                viewMode={viewMode}
                handleTileAction={handleTileAction}
              />
            </Box>
          );
        }
      })}
    </Flex>
  );
};
